import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CreatableSelect from 'react-select/creatable';
import CriteriaGrid from './CriteriaGrid';
import { useSelectStyles } from '../../utils/selectStyles';
import { buttonClass, ghostButtonClass, inputClass } from '../../pages/picture-ai/usePictureAi';

const COLUMN_LABELS = { item: 'Targeted item', shopsign: 'Shop sign' };
const STRICTNESS_LABELS = { lenient: 'Lenient', standard: 'Standard', strict: 'Strict' };
const DEFAULT_COLUMNS = ['item', 'shopsign'];

const normalized = (value) => `${value ?? ''}`.normalize('NFKC').replace(/\s+/gu, ' ').trim().toLocaleLowerCase('fr-FR');

const draftId = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

/**
 * The compliance agent's combinations: one complete setup per item, retailer, or both.
 *
 * Values are offered from what the programs received on the targeted questions, and a value not
 * seen yet can still be typed — a furniture reference often arrives after its configuration.
 */
const ComplianceTargeting = ({ value, onChange, disabled, widgetProps = {}, targetingValues = {}, onDraftCriteria }) => {
  const { t } = useTranslation();
  const selectStyles = useSelectStyles();
  const available = widgetProps.columns || DEFAULT_COLUMNS;
  const columns = value?.columns?.length ? value.columns : ['item'];
  const rules = Array.isArray(value?.rules) ? value.rules : [];
  const [open, setOpen] = useState(() => new Set());
  const [drafting, setDrafting] = useState(null);

  const idOf = (rule, index) => rule.key || rule._draft || `rule-${index}`;
  const emit = (patch) => onChange({ columns, rules, ...patch });
  const updateRule = (index, patch) => emit({ rules: rules.map((rule, position) => (position === index ? { ...rule, ...patch } : rule)) });
  const toggle = (id) => setOpen((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleColumn = (column) => {
    const next = columns.includes(column) ? columns.filter(candidate => candidate !== column) : [...columns, column];
    if (next.length === 0) return;
    emit({ columns: available.filter(candidate => next.includes(candidate)) });
  };

  const add = (template = null) => {
    const rule = {
      _draft: draftId(),
      match: {},
      prompt: template?.prompt || '',
      criteria: template?.criteria || [],
      strictness: template?.strictness || 'standard'
    };
    emit({ rules: [...rules, rule] });
    setOpen(current => new Set([...current, rule._draft]));
  };

  const remove = (index) => emit({ rules: rules.filter((_, position) => position !== index) });

  const draftCriteria = async (index) => {
    if (!onDraftCriteria) return;
    setDrafting(index);
    try {
      const criteria = await onDraftCriteria(rules[index].prompt);
      if (criteria) updateRule(index, { criteria });
    } finally {
      setDrafting(null);
    }
  };

  const optionsFor = useMemo(() => Object.fromEntries(available.map(column => [
    column,
    (targetingValues[column] || []).map(entry => ({ value: entry.label, label: `${entry.label} (${entry.pictures_count})`, count: entry.pictures_count }))
  ])), [available, targetingValues]);

  const countFor = (column, raw) => {
    if (!raw) return null;
    return (targetingValues[column] || []).find(entry => entry.value === normalized(raw))?.pictures_count ?? 0;
  };

  const describe = (rule) => columns
    .map(column => `${t(COLUMN_LABELS[column])} : ${rule.match?.[column] || t('any value')}`)
    .join(' · ');

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900 dark:bg-blue-950/40 dark:text-blue-100">
        {rules.length === 0
          ? t('No combination: the default setup below grades every picture of the scope.')
          : t('{{count}} combination(s): only the pictures matching one of them are analyzed, each with its own setup. The default setup below is not used.', { count: rules.length })}
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{t('Target on')}</span>
        {available.map(column => (
          <label key={column} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input type="checkbox" checked={columns.includes(column)} disabled={disabled} onChange={() => toggleColumn(column)} />
            {t(COLUMN_LABELS[column])}
          </label>
        ))}
      </div>
      {columns.length > 1 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t('When several combinations match a picture, the most precise one wins; at equal precision, the item wins over the shop sign.')}
        </p>
      )}

      {rules.map((rule, index) => {
        const id = idOf(rule, index);
        const expanded = open.has(id);
        return (
          <div key={id} className="rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap items-center justify-between gap-2 p-3">
              <button type="button" className="min-w-0 flex-1 text-left" onClick={() => toggle(id)}>
                <div className="truncate text-sm font-medium text-gray-900 dark:text-white">{describe(rule)}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {t('{{count}} criteria', { count: (rule.criteria || []).length })}
                  {columns.map((column) => {
                    const count = countFor(column, rule.match?.[column]);
                    return count === null ? null : <span key={column}> · {t(COLUMN_LABELS[column])} {t('{{count}} picture(s)', { count })}</span>;
                  })}
                  {rule.key && <code className="ml-2 text-[11px] text-gray-400">{rule.key}</code>}
                </div>
              </button>
              <div className="flex items-center gap-2">
                <button type="button" className={ghostButtonClass} onClick={() => toggle(id)}>{expanded ? t('Collapse') : t('Edit')}</button>
                <button type="button" className={ghostButtonClass} disabled={disabled || rules.length >= (widgetProps.maxRules || 100)} onClick={() => add(rule)}>{t('Duplicate')}</button>
                <button type="button" className="text-xs text-red-600 hover:underline disabled:opacity-50" disabled={disabled} onClick={() => remove(index)}>{t('Remove')}</button>
              </div>
            </div>

            {expanded && (
              <div className="space-y-4 border-t border-gray-200 p-3 dark:border-gray-700">
                <div className="grid gap-3 md:grid-cols-2">
                  {columns.map(column => (
                    <div key={column}>
                      <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">{t(COLUMN_LABELS[column])}</span>
                      <CreatableSelect
                        isClearable styles={selectStyles} isDisabled={disabled}
                        placeholder={t('any value')}
                        formatCreateLabel={input => t('Use « {{value}} »', { value: input })}
                        options={optionsFor[column]}
                        value={rule.match?.[column] ? { value: rule.match[column], label: rule.match[column] } : null}
                        onChange={option => updateRule(index, { match: { ...rule.match, [column]: option?.value || null } })}
                      />
                    </div>
                  ))}
                </div>

                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">{t('Compliance description')}<span className="ml-1 text-red-500">*</span></span>
                  <textarea
                    className={inputClass} rows={6} maxLength={8000} disabled={disabled}
                    value={rule.prompt || ''}
                    onChange={event => updateRule(index, { prompt: event.target.value })}
                  />
                </label>

                <CriteriaGrid
                  value={rule.criteria || []}
                  onChange={criteria => updateRule(index, { criteria })}
                  disabled={disabled}
                  onSuggest={onDraftCriteria ? () => draftCriteria(index) : undefined}
                  suggesting={drafting === index}
                />

                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">{t('Strictness')}</span>
                  <select className={inputClass} disabled={disabled} value={rule.strictness || 'standard'} onChange={event => updateRule(index, { strictness: event.target.value })}>
                    {(widgetProps.strictness || Object.keys(STRICTNESS_LABELS)).map(level => (
                      <option key={level} value={level}>{t(STRICTNESS_LABELS[level] || level)}</option>
                    ))}
                  </select>
                </label>
              </div>
            )}
          </div>
        );
      })}

      <button type="button" className={buttonClass} disabled={disabled || rules.length >= (widgetProps.maxRules || 100)} onClick={() => add()}>
        {t('Add a combination')}
      </button>
    </div>
  );
};

export default ComplianceTargeting;
